/**
 * =============================================================================
 * Componente: AffiliateLandingPage
 * Versão: 1.4.0 — Bloco "Indicado por" com avatar + comentário do afiliado
 *
 * Mudanças v1.3.0 (cirúrgicas):
 *   - normalizeForSubmit('phone', ...) agora aplica tetos rígidos por
 *     formato (BR especial: 11 dígitos; BR com DDI 55: 13; E.164: 15).
 *     Defesa em profundidade contra qualquer regressão upstream que
 *     pudesse permitir mais dígitos do que o usuário viu na tela
 *     (origem do bug do "1 final repetido" reportado na produção).
 *   - O PhoneInput (v1.1.0) já é a primeira linha de defesa.
 *
 * Mudanças v1.2.0:
 *   - Máscaras automáticas reutilizando utilitários do sistema
 *     (applyPhoneMask, applyCPFMask, applyCNPJMask, applyDateMask, applyCEPMask).
 *   - Validação robusta via validateInput (e-mail, telefone, CPF, CNPJ, data,
 *     número) com mensagens inline por campo.
 *   - Sanitização anti-injeção: remove `<` `>` em texto (prevenção XSS).
 *   - Anti-spam: bloqueia URLs (http/https/www) em campos de texto/textarea.
 *   - Normalização: trim em todos, lowercase no e-mail, dígitos puros para
 *     telefone/CPF/CNPJ/CEP no payload enviado.
 *   - Submit-guard: bloqueia duplo clique (lock + debounce de 1s).
 *   - Estados visuais por campo (borda destrutiva + mensagem inline).
 *
 *   Mantém: honeypot, time-to-submit, link_token (HMAC), countdown de redirect.
 * =============================================================================
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, CheckCircle2, Sparkles, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { createLogger } from '@/lib/logger';
import {
  applyPhoneMask,
  applyCPFMask,
  applyCNPJMask,
  applyDateMask,
  applyCEPMask,
} from '@/lib/input-masks';
import { validateInput } from '@/lib/input-validators';
import {
  validateAffiliatePhone as validateLandingPhone,
  normalizeAffiliatePhoneForSubmit,
} from '@/lib/affiliate-phone-validation';
import { cn } from '@/lib/utils';
import {
  useAffiliateBrandingPublic,
  buildBrandingCssVars,
} from '@/hooks/useAffiliateBranding';

const log = createLogger('AffiliateLandingPage');

/** Tipos de campos suportados pelo formulário dinâmico (v1.2.0). */
export type LandingFieldType =
  | 'text'
  | 'email'
  | 'phone'
  | 'textarea'
  | 'select'
  | 'cpf'
  | 'cnpj'
  | 'date'
  | 'cep'
  | 'number';

/** Definição de um campo dinâmico do formulário. */
export interface LandingFormField {
  key: string;
  label: string;
  type: LandingFieldType;
  required?: boolean;
  order?: number;
  placeholder?: string;
  options?: { value: string; label: string }[];
}

export interface AffiliateLandingProps {
  /** Token opaco assinado (HMAC) emitido pelo track-click — substitui linkId em v1.1.0. */
  linkToken: string;
  /** Cookie 1st-party já gravado. */
  cookieId: string | null;
  /** Branding e textos vindos da campanha. */
  branding: {
    tenantName: string | null;
    tenantLogoUrl: string | null;
    /** Slug público do tenant — alimenta o branding (cores) personalizado. */
    tenantSlug?: string | null;
    title: string | null;
    subtitle: string | null;
    heroImageUrl: string | null;
    ctaLabel: string;
    thankYouTitle: string;
    thankYouMessage: string;
    /** v1.4.0: foto pública do afiliado (humanização do form). */
    affiliateAvatarUrl?: string | null;
    /** v1.4.0: comentário público (testimonial) do afiliado. */
    affiliateBioComment?: string | null;
  };
  /** Configuração dos campos. */
  formFields: LandingFormField[];
  /** UTMs lidos da URL (encaminhados ao backend). */
  utms: Record<string, string | null>;
}

interface SubmitResponse {
  ok?: boolean;
  redirect_url?: string | null;
  redirect_after_secs?: number | null;
  thank_you_title?: string;
  thank_you_message?: string;
  error?: string;
}

/** Regex anti-spam: detecta URLs em campos de texto livre. */
const URL_REGEX = /(https?:\/\/|www\.)\S+/i;

/**
 * Validação específica de telefone para a landing pública.
 *
 * Recebe o valor já produzido pelo PhoneInput (que pode incluir DDI),
 * normaliza para apenas dígitos e aceita os formatos abaixo.
 *
 * Aceita:
 *  - Brasil sem DDI: 10 (fixo) ou 11 (celular) dígitos.
 *  - Brasil com DDI 55: 12 ou 13 dígitos (55 + DDD + número).
 *  - Internacional: 8 a 15 dígitos (E.164).
 *  - Especiais BR (sem DDI): 0800, 4003, 400X, 300X.
 *
 * Rejeita:
 *  - Vazio.
 *  - Comprimento fora de 8 a 15 dígitos.
 *  - Sequência repetida (ex: 11111111111).
 *  - DDD brasileiro inválido (< 11) quando aplicável.
 *
 * IMPORTANTE: NÃO usar o validador legado validateInput('phone') aqui,
 * pois ele rejeita formatos com DDI emitidos pelo PhoneInput.
 */
// validateLandingPhone foi extraído para src/lib/affiliate-phone-validation.ts
// (v2.0.0). É reimportado no topo deste arquivo via alias para preservar os
// call-sites existentes sem alterar comportamento.

/** Sanitiza string removendo `<` `>` (anti-XSS leve antes do envio). */
function sanitizeText(value: string): string {
  return value.replace(/[<>]/g, '');
}

/** Aplica a máscara apropriada ao tipo do campo. */
function applyMaskForType(type: LandingFieldType, raw: string): string {
  switch (type) {
    case 'phone':
      return applyPhoneMask(raw);
    case 'cpf':
      return applyCPFMask(raw);
    case 'cnpj':
      return applyCNPJMask(raw);
    case 'date':
      return applyDateMask(raw);
    case 'cep':
      return applyCEPMask(raw);
    case 'email':
      // E-mail: bloqueia espaços e caracteres < >.
      return sanitizeText(raw).replace(/\s+/g, '').toLowerCase();
    case 'number':
      return raw.replace(/[^\d.,-]/g, '');
    case 'text':
    case 'textarea':
      return sanitizeText(raw);
    default:
      return raw;
  }
}

/** Placeholder padrão por tipo (caso o admin não tenha definido). */
function defaultPlaceholder(type: LandingFieldType): string {
  switch (type) {
    case 'phone':
      return '(11) 99999-9999';
    case 'email':
      return 'voce@exemplo.com';
    case 'cpf':
      return '123.456.789-00';
    case 'cnpj':
      return '12.345.678/0001-90';
    case 'date':
      return 'DD/MM/AAAA';
    case 'cep':
      return '01310-100';
    case 'number':
      return '0';
    default:
      return '';
  }
}

/** Mapeia tipo do campo → tipo aceito pelo validateInput. */
function toValidatorType(
  type: LandingFieldType,
): 'text' | 'number' | 'email' | 'phone' | 'cpf' | 'cnpj' | 'date' {
  switch (type) {
    case 'email':
    case 'phone':
    case 'cpf':
    case 'cnpj':
    case 'date':
    case 'number':
      return type;
    default:
      return 'text';
  }
}

/**
 * Normaliza valor para envio ao backend (remove máscaras quando aplicável).
 *
 * v1.3.0 — Defesa em profundidade para telefone:
 *   Mesmo que o PhoneInput (v1.1.0) já garanta no máximo 11 dígitos locais,
 *   reforçamos o teto aqui para que qualquer regressão em componentes
 *   reaproveitados não permita gravar telefones com dígito extra (causa do
 *   bug histórico de "1 final repetido" na landing de indicação).
 *
 * Regras (BR):
 *   - DDI 55 + DDD + número (até 11 locais) → máximo 13 dígitos.
 *   - Sem DDI 55 (ex: 0800/4003/400X/300X) → máximo 11 dígitos.
 *   - Internacional (não-BR): respeita até 15 dígitos (E.164).
 */
function normalizeForSubmit(type: LandingFieldType, value: string): string {
  const trimmed = value.trim();
  switch (type) {
    case 'phone': {
      // Delegado ao util compartilhado (mesma regra do modal "Nova Indicação").
      return normalizeAffiliatePhoneForSubmit(trimmed);
    }
    case 'cpf':
    case 'cnpj':
    case 'cep':
      return trimmed.replace(/\D/g, '');
    case 'email':
      return trimmed.toLowerCase();
    default:
      return trimmed;
  }
}

export function AffiliateLandingPage({
  linkToken,
  cookieId,
  branding,
  formFields,
  utms,
}: AffiliateLandingProps) {
  // Ordena os campos pelo `order` (estável caso ausente)
  const sortedFields = useMemo(
    () =>
      [...formFields].sort(
        (a, b) => (a.order ?? 999) - (b.order ?? 999),
      ),
    [formFields],
  );

  // Branding personalizado do tenant (cores + logo) — busca pública sem auth.
  const { data: tenantBranding } = useAffiliateBrandingPublic({
    tenantSlug: branding.tenantSlug ?? null,
  });
  const visual = tenantBranding?.branding ?? null;
  const brandingStyle = useMemo(
    () => buildBrandingCssVars(visual),
    [visual],
  );
  // Logo prioriza branding do tenant; fallback para a logo já vinda do payload.
  const logoUrl = visual?.logo_url ?? branding.tenantLogoUrl ?? null;

  const [values, setValues] = useState<Record<string, string>>({});
  /** Erros por campo (validação inline). */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Honeypot: se preenchido, é bot. Mantemos no state para não poluir o DOM externo.
  const [honeypot, setHoneypot] = useState('');
  // Marca o instante em que o componente montou (anti-bot por time-to-submit).
  const mountedAtRef = useMemo(() => ({ value: Date.now() }), []);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<SubmitResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  /** Lock anti-duplo-submit: timestamp do último envio aceito. */
  const lastSubmitAtRef = useRef<number>(0);

  // Countdown para redirect pós-submit (quando configurado)
  useEffect(() => {
    if (!submitted?.redirect_url || !submitted.redirect_after_secs) return;
    setCountdown(submitted.redirect_after_secs);
    const interval = window.setInterval(() => {
      setCountdown((c) => {
        if (c === null) return null;
        if (c <= 1) {
          window.clearInterval(interval);
          window.location.href = submitted.redirect_url!;
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [submitted]);

  /** Handler unificado de mudança com máscara + sanitização. */
  function handleFieldChange(field: LandingFormField, raw: string) {
    const masked = applyMaskForType(field.type, raw);
    setValues((v) => ({ ...v, [field.key]: masked }));
    // Limpa erro ao digitar
    if (fieldErrors[field.key]) {
      setFieldErrors((e) => {
        const next = { ...e };
        delete next[field.key];
        return next;
      });
    }
  }

  /** Validação completa do formulário (retorna mapa de erros). */
  function validateAll(): Record<string, string> {
    const errors: Record<string, string> = {};
    for (const field of sortedFields) {
      const raw = (values[field.key] ?? '').trim();

      // Obrigatório?
      if (field.required && !raw) {
        errors[field.key] = `${field.label} é obrigatório`;
        continue;
      }
      if (!raw) continue;

      // Anti-spam: URLs em texto livre.
      if ((field.type === 'text' || field.type === 'textarea') && URL_REGEX.test(raw)) {
        errors[field.key] = 'Links não são permitidos neste campo';
        continue;
      }

      // CEP usa validação custom (não está no validateInput).
      if (field.type === 'cep') {
        const digits = raw.replace(/\D/g, '');
        if (digits.length !== 8) {
          errors[field.key] = 'CEP deve conter 8 dígitos (ex: 01234-567)';
        }
        continue;
      }

      // Telefone: validador específico da landing (compatível com DDI do PhoneInput).
      // Não usar validateInput('phone') legado aqui — rejeita formatos com DDI.
      if (field.type === 'phone') {
        const phoneResult = validateLandingPhone(raw);
        if (!phoneResult.isValid) {
          errors[field.key] = phoneResult.error ?? `${field.label} inválido`;
        }
        continue;
      }

      // Validação dos demais tipos suportados.
      const validatorType = toValidatorType(field.type);
      if (validatorType !== 'text') {
        const result = validateInput(raw, validatorType);
        if (!result.isValid) {
          errors[field.key] = result.error ?? `${field.label} inválido`;
        }
      }
    }
    return errors;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Lock anti-duplo-submit (debounce de 1s).
    const now = Date.now();
    if (submitting || now - lastSubmitAtRef.current < 1000) {
      return;
    }

    const errors = validateAll();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Corrija os campos destacados antes de enviar.');
      return;
    }
    setFieldErrors({});
    lastSubmitAtRef.current = now;

    // Normaliza payload (strip de máscaras + lowercase no e-mail).
    const normalizedForm: Record<string, string> = {};
    for (const field of sortedFields) {
      const raw = values[field.key];
      if (!raw) continue;
      normalizedForm[field.key] = normalizeForSubmit(field.type, raw);
    }

    setSubmitting(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
      const endpoint = `${supabaseUrl}/functions/v1/affiliate-submit-lead`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: anonKey,
          Authorization: `Bearer ${anonKey}`,
        },
        body: JSON.stringify({
          link_token: linkToken,
          cookie_id: cookieId,
          form: normalizedForm,
          // Anti-bot: honeypot + tempo decorrido desde mount.
          website: honeypot,
          elapsed_ms: Date.now() - mountedAtRef.value,
          utm_source: utms.utm_source,
          utm_medium: utms.utm_medium,
          utm_campaign: utms.utm_campaign,
          utm_content: utms.utm_content,
          utm_term: utms.utm_term,
        }),
      });

      const data = (await res.json()) as SubmitResponse;
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Falha ao enviar formulário');
      }

      setSubmitted(data);
    } catch (err: any) {
      log.error('Erro no submit da landing', err);
      setError(err?.message || 'Não foi possível enviar. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  }

  // ========== Render: tela de agradecimento ==========
  if (submitted) {
    return (
      <div
        className="affiliate-shell min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-muted/30 to-background"
        style={brandingStyle}
      >
        <div className="max-w-md w-full text-center space-y-6 bg-card border border-border/40 rounded-2xl p-8 shadow-lg">
          <div
            className="mx-auto h-16 w-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'hsl(var(--aff-primary) / 0.12)' }}
          >
            <CheckCircle2
              className="h-8 w-8"
              style={{ color: 'hsl(var(--aff-primary))' }}
            />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">
              {submitted.thank_you_title || branding.thankYouTitle}
            </h1>
            <p className="text-muted-foreground">
              {submitted.thank_you_message || branding.thankYouMessage}
            </p>
          </div>
          {countdown !== null && submitted.redirect_url && (
            <p className="text-xs text-muted-foreground">
              Redirecionando em {countdown}s…
            </p>
          )}
        </div>
      </div>
    );
  }

  // ========== Render: formulário (layout premium inspirado em PublicScheduling) ==========
  return (
    <div
      className="affiliate-shell min-h-screen bg-gradient-to-b from-muted/30 to-background"
      style={brandingStyle}
    >
      <div className="mx-auto max-w-2xl px-4 py-8 sm:py-12">
        {/* Header premium centralizado */}
        <div className="text-center mb-8 px-2">
          {/* Logo (formato barra) ou ícone fallback com cor primária */}
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={branding.tenantName ?? 'Logo'}
              className="mx-auto mb-4 max-h-14 sm:max-h-16 max-w-[240px] sm:max-w-[280px] object-contain"
            />
          ) : (
            <div
              className="h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md ring-1 ring-border/50"
              style={{ backgroundColor: 'hsl(var(--aff-primary) / 0.12)' }}
            >
              <Sparkles
                className="h-7 w-7"
                style={{ color: 'hsl(var(--aff-primary))' }}
              />
            </div>
          )}

          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
            {branding.title || branding.tenantName || 'Cadastre-se'}
          </h1>

          {branding.subtitle && (
            <p className="text-muted-foreground mt-2 max-w-lg mx-auto text-sm sm:text-base leading-relaxed break-words">
              {branding.subtitle}
            </p>
          )}

          {/* Hero image opcional como banner sutil abaixo do subtítulo */}
          {branding.heroImageUrl && (
            <div
              className="mt-5 h-28 sm:h-32 rounded-2xl overflow-hidden ring-1 ring-border/40"
              style={{
                backgroundImage: `url(${branding.heroImageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
              aria-hidden="true"
            />
          )}
        </div>

        {/* =====================================================================
            Bloco "Indicado por" — humaniza a captação com foto + comentário
            público do afiliado. Renderizado APENAS quando o afiliado preencheu
            ao menos um dos dois campos. Ausência = decisão consciente do
            afiliado de não exibir aquele dado. (v1.4.0)
            ===================================================================== */}
        {(branding.affiliateAvatarUrl || branding.affiliateBioComment) && (
          <div className="mb-8 flex flex-col items-center gap-4 px-2">
            {branding.affiliateAvatarUrl && (
              <div
                className="rounded-full p-1 shadow-md"
                style={{
                  background: 'var(--aff-gradient-hero, hsl(var(--aff-primary)))',
                }}
              >
                <SmartAvatar
                  src={branding.affiliateAvatarUrl}
                  size="2xl"
                  className="ring-2 ring-background"
                  alt="Foto do afiliado"
                />
              </div>
            )}

            {branding.affiliateBioComment && (
              <div className="relative max-w-lg w-full bg-card border border-border/40 rounded-2xl px-5 py-4 shadow-sm">
                <Quote
                  className="absolute -top-3 left-4 h-6 w-6 p-1 rounded-full bg-card border border-border/40"
                  style={{ color: 'hsl(var(--aff-primary))' }}
                  aria-hidden="true"
                />
                <p className="text-sm sm:text-[15px] text-foreground/85 italic leading-relaxed whitespace-pre-wrap text-center">
                  {sanitizeText(branding.affiliateBioComment)}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Card formulário */}
        <main>
          <form
            onSubmit={handleSubmit}
            className="space-y-4 bg-card border border-border/40 rounded-2xl p-6 sm:p-8 shadow-lg"
            noValidate
          >
          {/* Honeypot anti-bot: invisível para humanos, atrativo para bots. */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '-9999px',
              width: '1px',
              height: '1px',
              overflow: 'hidden',
            }}
          >
            <label htmlFor="website">Website (não preencha)</label>
            <input
              id="website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          {sortedFields.map((field) => {
            const fieldError = fieldErrors[field.key];
            const placeholder = field.placeholder || defaultPlaceholder(field.type);
            const inputId = `f-${field.key}`;

            // ---- Textarea ----
            if (field.type === 'textarea') {
              return (
                <div key={field.key} className="space-y-1.5">
                  <Label htmlFor={inputId}>
                    {field.label}
                    {field.required && (
                      <span className="text-destructive ml-1">*</span>
                    )}
                  </Label>
                  <Textarea
                    id={inputId}
                    value={values[field.key] ?? ''}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    placeholder={placeholder}
                    rows={4}
                    maxLength={2000}
                    aria-invalid={!!fieldError}
                    className={cn(fieldError && 'border-destructive focus-visible:ring-destructive')}
                  />
                  {fieldError && (
                    <p className="text-xs text-destructive">{fieldError}</p>
                  )}
                </div>
              );
            }

            // ---- Select ----
            if (field.type === 'select' && field.options) {
              return (
                <div key={field.key} className="space-y-1.5">
                  <Label htmlFor={inputId}>
                    {field.label}
                    {field.required && (
                      <span className="text-destructive ml-1">*</span>
                    )}
                  </Label>
                  <select
                    id={inputId}
                    className={cn(
                      'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
                      fieldError && 'border-destructive',
                    )}
                    value={values[field.key] ?? ''}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, [field.key]: e.target.value }))
                    }
                    aria-invalid={!!fieldError}
                  >
                    <option value="">Selecione…</option>
                    {field.options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {fieldError && (
                    <p className="text-xs text-destructive">{fieldError}</p>
                  )}
                </div>
              );
            }

            // ---- Inputs com máscara/validação ----
            // Limites de tamanho por tipo (proteção adicional).
            const maxLengthByType: Partial<Record<LandingFieldType, number>> = {
              phone: 20,
              cpf: 14,
              cnpj: 18,
              cep: 9,
              date: 10,
              email: 200,
              number: 20,
              text: 200,
            };

            const htmlType =
              field.type === 'email'
                ? 'email'
                : field.type === 'phone'
                ? 'tel'
                : field.type === 'number'
                ? 'text' // mantemos text para preservar máscara/decimais
                : 'text';

            const inputMode =
              field.type === 'phone' ||
              field.type === 'cpf' ||
              field.type === 'cnpj' ||
              field.type === 'cep'
                ? 'numeric'
                : field.type === 'number'
                ? 'decimal'
                : field.type === 'email'
                ? 'email'
                : 'text';

            // Telefone usa PhoneInput (com DDI) — padrão do sistema (igual ao agendamento público).
            // Garante envio em E.164 (+55...) e dedup correto no backend.
            if (field.type === 'phone') {
              return (
                <div key={field.key} className="space-y-1.5">
                  <Label htmlFor={inputId}>
                    {field.label}
                    {field.required && (
                      <span className="text-destructive ml-1">*</span>
                    )}
                  </Label>
                  <PhoneInput
                    id={inputId}
                    value={values[field.key] ?? ''}
                    onChange={(value) => {
                      // Atualiza valor (E.164 com DDI, ex: +5511987387787)
                      setValues((v) => ({ ...v, [field.key]: value }));
                      // Limpa erro ao digitar (mesmo padrão de handleFieldChange)
                      if (fieldErrors[field.key]) {
                        setFieldErrors((e) => {
                          const next = { ...e };
                          delete next[field.key];
                          return next;
                        });
                      }
                    }}
                    placeholder={placeholder || '(11) 99999-9999'}
                    aria-invalid={!!fieldError}
                    className={cn(fieldError && 'border-destructive focus-visible:ring-destructive')}
                  />
                  {fieldError && (
                    <p className="text-xs text-destructive">{fieldError}</p>
                  )}
                </div>
              );
            }

            return (
              <div key={field.key} className="space-y-1.5">
                <Label htmlFor={inputId}>
                  {field.label}
                  {field.required && (
                    <span className="text-destructive ml-1">*</span>
                  )}
                </Label>
                <Input
                  id={inputId}
                  type={htmlType}
                  value={values[field.key] ?? ''}
                  onChange={(e) => handleFieldChange(field, e.target.value)}
                  placeholder={placeholder}
                  inputMode={inputMode as React.HTMLAttributes<HTMLInputElement>['inputMode']}
                  maxLength={maxLengthByType[field.type] ?? 200}
                  autoComplete={
                    field.type === 'email' ? 'email' : 'off'
                  }
                  aria-invalid={!!fieldError}
                  className={cn(fieldError && 'border-destructive focus-visible:ring-destructive')}
                />
                {fieldError && (
                  <p className="text-xs text-destructive">{fieldError}</p>
                )}
              </div>
            );
          })}

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
              {error}
            </p>
          )}

          {/* CTA premium — usa o gradiente do branding do tenant (mesmo padrão
              do botão "Entrar" do login do afiliado), garantindo identidade
              visual consistente em todas as páginas públicas de indicação. */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 rounded-xl text-[hsl(var(--aff-primary-foreground))] hover:opacity-90 border-0 font-semibold text-[15px] shadow-[var(--aff-shadow-soft)] transition-opacity"
            style={{ background: 'var(--aff-gradient-hero)' }}
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {branding.ctaLabel}
          </Button>
          </form>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Indicado por <strong>{branding.tenantName ?? 'parceiro'}</strong>
          </p>
        </main>
      </div>
    </div>
  );
}
